import { Hero, HeroCol, HeroTitle } from "../../components/Hero";
import {
  Section,
  SectionCol,
  SectionList,
  SectionListItem,
  SectionHeading,
  SectionParagraph,
  SectionImage,
  SectionTitle,
} from "../../components/Section";
import { CtaLinkButton, LinkButton } from "../../components/Button";
import { PlatformDiagram } from "./components/PlatformDiagram";
import { Customers } from "../../components/subcomponents/Customers";
import { SecurityCard } from "../../components/subcomponents/SecurityCard";
import { SecurityBadges } from "../../components/subcomponents/SecurityBadges";
import { BottomCta } from "../../components/subcomponents/BottomCta";
import Content from "./assets/Content.svg";
import Content1 from "./assets/Content-1.svg";
import Content4 from "./assets/Content-4.svg";
import Content5 from "./assets/Content-5.svg";
import Content6 from "./assets/Content-6.svg";
import Content7 from "./assets/Content-7.svg";
import { FiSettings, FiLink, FiDatabase, FiHardDrive } from "react-icons/fi";
import PlatformOverview from "./assets/platform_diagram.svg";
import styles from "./Platform.module.less";

export const Platform = () => {
  const data = [
    {
      id: "Connectors",
      icon: FiLink,
      title: "Connectors as a single interface to any upstream source",
      description:
        "Remove the complexity of managing different sources of data and their metadata by delivering a single, unified experience of connecting to any API, bulk file or stream",
    },
    {
      id: "Data APIs",
      icon: FiDatabase,
      title: "Data APIs for real-time data access in production workflows",
      description:
        "Deploy a single connector or an orchestration of many together, as an API. Simply build with Demyst's proprietary configuration language, integrate through a standardised Demyst REST endpoint, and manage with in-built logging, monitoring and alerting",
    },
    {
      id: "Data Shares",
      icon: FiHardDrive,
      title: "Data shares for delivering bulk data into landing zones",
      description:
        "Rapidly access bulk files of data in your landing zone, configured to your needs with the format, schema, scheduling and more, such as as a Snowflake table share or into a AWS S3 bucket",
    },
    {
      id: "Configuration Gateway",
      icon: FiSettings,
      title: "All managed through our Configuration Gateway APIs",
      description:
        "Simply manage and maintain complete visibility of all your data, through the configuration gateway. Managed through your own version control systems and access secured with multiple levels of controls",
    },
  ];

  return (
    <>
      <Hero background="violet-800" withStarsAnimation>
        <HeroCol>
          <HeroTitle>External Data Orchestration</HeroTitle>
          <br></br>
          <br></br>
          <br></br>
          <CtaLinkButton data-test-id="platform-hero-button" />
          <PlatformDiagram />
          <Customers withMargin />
        </HeroCol>
      </Hero>

      <Section
        background="blue-25"
        type="two-col"
        masthead={{
          title: "The Platform to manage all your external data",
        }}
        reverse
      >
        <SectionCol>
          <SectionList>
            {data.map((item, index) => (
              <SectionListItem icon={item.icon} key={`${item.id}_${index}`}>
                <SectionHeading>{item.title}</SectionHeading>
                <SectionParagraph>{item.description}</SectionParagraph>
              </SectionListItem>
            ))}
          </SectionList>
          <LinkButton autoWidth href={"https://docs.demyst.com/"} variant="inverted">
            Learn more in our docs
          </LinkButton>
        </SectionCol>
        <SectionCol className={styles.PlatformOverviewImage}>
          <SectionImage
            src={PlatformOverview.src}
            alt=""
            width="447"
            height="330"
            layout="responsive"
          />
        </SectionCol>
      </Section>
      <Section type="one-col" background="coolGray-50" removeBottomPadding>
        <SectionTitle>Powerful features that make the difference</SectionTitle>
      </Section>
      <Section reverseMobile type="two-col" background="coolGray-50">
        <SectionCol>
          <SectionImage src={Content.src} alt="" width="700" height="425" />
        </SectionCol>
        <SectionCol>
          <SectionHeading type="h3">A standardized schema for external data</SectionHeading>

          <SectionList size="sm" background="blue-25">
            <SectionListItem>
              <SectionParagraph>
                Connectors remove the complexity of external data integration with standardised
                ingress, an automated LLM-enabled way of generating a standardised connection and
                relevant metadata for any upstream data source
              </SectionParagraph>
            </SectionListItem>
            <SectionListItem>
              <SectionParagraph>
                Leverages automatic schema detection to create data dictionaries and apply Demyst's
                attribute types that improve match rates
              </SectionParagraph>
            </SectionListItem>
            <SectionListItem>
              <SectionParagraph>
                Detect and flag anomalies when ingesting data to remove downstream risks
              </SectionParagraph>
            </SectionListItem>
          </SectionList>
        </SectionCol>
      </Section>

      <Section reverseMobile type="two-col" background="white" reverse>
        <SectionCol>
          <SectionImage src={Content6.src} alt="" width="700" height="425" />
        </SectionCol>
        <SectionCol>
          <SectionHeading type="h3">Metadata required for external data workflows</SectionHeading>
          <SectionList size="sm" background="blue-25">
            <SectionListItem>
              <SectionParagraph>
                Extensive connector metadata means remaining in control of your Data Hub and
                fostering data use. Access data dictionaries, performance statistics, usage logs,
                and governance metadata such as usage rights
              </SectionParagraph>
            </SectionListItem>
            <SectionListItem>
              <SectionParagraph>
                Integrates with other enterprise data catalogs such as Informatica, Collibra and
                Alation, simplifying metadata management
              </SectionParagraph>
            </SectionListItem>
          </SectionList>
        </SectionCol>
      </Section>

      <Section reverseMobile type="two-col" background="coolGray-50">
        <SectionCol>
          <SectionImage src={Content1.src} alt="" width="700" height="425" />
        </SectionCol>
        <SectionCol>
          <SectionHeading type="h3">External data resilience</SectionHeading>
          <SectionList size="sm" background="blue-25">
            <SectionListItem>
              <SectionParagraph>
                Use external data orchestration across multiple sources, creating a single Data API
                that optimizes for cost or includes redundancies with water-falling, failovers and
                more
              </SectionParagraph>
            </SectionListItem>
            <SectionListItem>
              <SectionParagraph>
                Avoid managing a complex array of endpoints by integrating with Demyst's
                standardised REST endpoint
              </SectionParagraph>
            </SectionListItem>
            <SectionListItem>
              <SectionParagraph>
                Automatically cache data you've already called to save on data costs
              </SectionParagraph>
            </SectionListItem>
          </SectionList>
        </SectionCol>
      </Section>

      <Section reverse reverseMobile type="two-col" background="white">
        <SectionCol>
          <SectionImage src={Content4.src} alt="" width="600" height="380" />
        </SectionCol>
        <SectionCol>
          <SectionHeading type="h3">Batch data workflows</SectionHeading>
          <SectionList size="sm" background="blue-25">
            <SectionListItem>
              <SectionParagraph>
                Easily schedule Data shares to access bulk data from multiple sources in your data
                lakes and warehouses
              </SectionParagraph>
            </SectionListItem>
            <SectionListItem>
              <SectionParagraph>
                Reduce the time it takes to ingest and manipulate data into a workable data product.
                Transform upstream data from any source into the &quot;agreed in advance&quot;
                standardised format, naming conventions, encoding, record validation methods, retry
                mechanism and more
              </SectionParagraph>
            </SectionListItem>
          </SectionList>
        </SectionCol>
      </Section>

      <Section reverseMobile type="two-col" background="coolGray-50">
        <SectionCol>
          <SectionImage src={Content5.src} alt="" width="580" height="400" />
        </SectionCol>
        <SectionCol>
          <SectionHeading type="h3">
            No more errant data with external data visibility
          </SectionHeading>
          <SectionList size="sm" background="blue-25">
            <SectionListItem>
              <SectionParagraph>
                Have complete control of all your data and its use, manage incidents with alerts,
                and ensure governance compliance. Fueled by detailed logging of all data movements
              </SectionParagraph>
            </SectionListItem>
            <SectionListItem>
              <SectionParagraph>
                Enterprise grade security functionality helps keep data security at the forefront,
                including SSO, MFA, user access controls, IP whitelists, and more
              </SectionParagraph>
            </SectionListItem>
            <SectionListItem>
              <SectionParagraph>
                Simplify support with a standardised error layer across all data sources. Trust in
                Demyst frontline support to triage and manage complex multi-provider incidents
              </SectionParagraph>
            </SectionListItem>
          </SectionList>
        </SectionCol>
      </Section>

      <Section reverse reverseMobile type="two-col" background="white">
        <SectionCol>
          <SectionImage src={Content7.src} alt="" width="580" height="400" />
        </SectionCol>
        <SectionCol>
          <SectionHeading type="h3">Accelerated data licensing for Discovery</SectionHeading>
          <SectionList size="sm" background="blue-25">
            <SectionListItem>
              <SectionParagraph>
                Ensure there is a compliant path for business users to leverage for external data
                evaluation in a reasonable timeframe
              </SectionParagraph>
            </SectionListItem>
            <SectionListItem>
              <SectionParagraph>
                Derisk your data evaluation backlog. Demyst provides data procurement, due
                dilligence and ingestion services for evaluation data, completed to best practice
                standards
              </SectionParagraph>
            </SectionListItem>
            <SectionListItem>
              <SectionParagraph>
                Ensure compliance with proper data use by ingesting this information as Connector
                metadata
              </SectionParagraph>
            </SectionListItem>
          </SectionList>
        </SectionCol>
      </Section>

      <Section type="one-col" background="white" removeTopPadding>
        <LinkButton autoWidth href={"https://docs.demyst.com/"} variant="inverted">
          Learn more in our docs
        </LinkButton>
      </Section>

      <Section
        background="coolGray-100"
        type="one-col"
        masthead={{
          title: "Security you can trust",
          subtitle:
            "Demyst is dedicated to secure and compliant data use to support critical business decisions. It maintains dedicated Security, Compliance, and IT teams that are empowered to act.",
        }}
      >
        <SectionCol>
          <SecurityCard />
          <SectionParagraph type="subheading">
            <span>
              <span>
                Demyst is SOC 2 Type II Certified and maintains an information security program
                based on established security standards such as <strong> NIST 800-53</strong> and{" "}
                <strong>ISO 27001</strong>.
              </span>
            </span>
          </SectionParagraph>
        </SectionCol>
      </Section>

      <Section background="blue-800" type="one-col">
        <SectionCol>
          <BottomCta />
        </SectionCol>
      </Section>

      <Section background="blue-1000" removeBottomPadding removeTopPadding>
        <SectionCol>
          <SecurityBadges />
        </SectionCol>
      </Section>
    </>
  );
};
